/**
 * The dashboard field catalog: the single whitelist of everything a card may read.
 *
 * Each entity declares the RBAC area a viewer must be able to read, the fields that can
 * be filtered / listed / grouped, and how to build an in-app link to one record. The
 * backend maps `path` values onto typed Prisma queries — nothing here reaches raw SQL,
 * and a field absent from this catalog is unreachable from a card.
 *
 * Adding an entity: append a {@link DashboardEntityDef} here. No backend change is needed
 * unless the entity introduces a new Prisma model mapping (see the backend engine's
 * `ENTITY_DELEGATES`).
 */
import { DASHBOARD_ENTITY_LABELS } from './dashboard.js';
import { ONBOARDING_PHASES, ONBOARDING_PHASE_LABELS, ONBOARDING_STATUSES, ONBOARDING_STATUS_LABELS, ONBOARDING_STEPS, } from './onboarding.js';
import { PROJECT_STATUSES } from './project.js';
import { AGREEMENT_STATUSES, AGREEMENT_TYPES, AGREEMENT_TYPE_LABELS } from './agreement.js';
import { INVOICE_DOCUMENT_TYPES, INVOICE_DOCUMENT_TYPE_LABELS, INVOICE_SOURCES, INVOICE_STATUSES, } from './invoice.js';
import { OPPORTUNITY_KINDS, OPPORTUNITY_KIND_LABELS, OPPORTUNITY_STAGES, OPPORTUNITY_STAGE_LABELS, OPPORTUNITY_STATUSES, OPPORTUNITY_STATUS_LABELS, } from './opportunity.js';
import { QUOTE_STATUSES, QUOTE_STATUS_LABELS } from './quote.js';
import { TICKET_ORIGINS, TICKET_PRIORITIES } from './ticket.js';
import { TIMESHEET_STATUSES } from './timesheet.js';
import { ADMIN_ACCESS_LABELS, ADMIN_ACCESS_LEVELS, AI_ACCESS_LABELS, AI_ACCESS_LEVELS, SUPPORT_TICKET_ACCESS_LABELS, SUPPORT_TICKET_ACCESS_LEVELS, } from './contact.js';
// ---------------------------------------------------------------------------
// Small helpers for building option lists
// ---------------------------------------------------------------------------
function options(values, labels) {
    return values.map((value) => ({ value, label: labels?.[value] ?? titleCase(value) }));
}
function titleCase(value) {
    return value
        .split('_')
        .map((part) => (part ? part.charAt(0).toUpperCase() + part.slice(1) : part))
        .join(' ');
}
const SOURCE_OPTIONS = [
    { value: 'native', label: 'Created in PSA' },
    { value: 'connectwise', label: 'Imported from ConnectWise' },
];
const ENTRY_METHOD_OPTIONS = [
    { value: 'timesheet', label: 'Timesheet (PSA)' },
    { value: 'project_portal', label: 'Public project link' },
    { value: 'ticket_portal', label: 'Public ticket link' },
    { value: 'connectwise', label: 'ConnectWise sync' },
];
/** `createdAt` / `updatedAt`, which every entity exposes identically. */
function timestampFields() {
    return [
        { key: 'createdAt', label: 'Created', type: 'datetime', path: 'createdAt', sortable: true },
        { key: 'updatedAt', label: 'Last updated', type: 'datetime', path: 'updatedAt', sortable: true },
    ];
}
/** A relation-name field that groups on its local foreign key so totals stay exact. */
function lookupNameField(args) {
    return {
        key: args.key,
        label: args.label,
        type: 'string',
        path: args.path,
        groupable: true,
        groupPath: args.groupPath,
        groupLookup: args.lookup,
        sortable: true,
        defaultColumn: args.defaultColumn,
    };
}
// ---------------------------------------------------------------------------
// Projects
// ---------------------------------------------------------------------------
const PROJECT_DEF = {
    entity: 'projects',
    label: DASHBOARD_ENTITY_LABELS.projects,
    area: 'projects',
    recordPath: '/projects/{id}',
    defaultSort: { field: 'number', dir: 'desc' },
    fields: [
        { key: 'number', label: 'Number', type: 'number', path: 'number', sortable: true, defaultColumn: true },
        { key: 'name', label: 'Project name', type: 'string', path: 'name', sortable: true, defaultColumn: true },
        lookupNameField({
            key: 'customerName',
            label: 'Customer',
            path: 'customer.name',
            groupPath: 'customerId',
            lookup: 'customer',
            defaultColumn: true,
        }),
        {
            key: 'customerId',
            label: 'Customer (pick one)',
            type: 'uuid',
            path: 'customerId',
            lookup: 'customer',
            column: false,
        },
        {
            key: 'status',
            label: 'Status',
            type: 'enum',
            path: 'status',
            options: options(PROJECT_STATUSES),
            groupable: true,
            sortable: true,
            defaultColumn: true,
        },
        lookupNameField({
            key: 'projectManagerName',
            label: 'Project manager',
            path: 'projectManager.displayName',
            groupPath: 'projectManagerId',
            lookup: 'member',
            defaultColumn: true,
        }),
        {
            key: 'projectManagerId',
            label: 'Project manager (pick one)',
            type: 'uuid',
            path: 'projectManagerId',
            lookup: 'member',
            column: false,
        },
        lookupNameField({
            key: 'serviceCategoryName',
            label: 'Service category',
            path: 'defaultServiceCategory.name',
            groupPath: 'defaultServiceCategoryId',
            lookup: 'service_category',
        }),
        {
            key: 'defaultServiceCategoryId',
            label: 'Service category (pick one)',
            type: 'uuid',
            path: 'defaultServiceCategoryId',
            lookup: 'service_category',
            column: false,
        },
        lookupNameField({
            key: 'billingClassName',
            label: 'Billing class',
            path: 'defaultBillingClass.name',
            groupPath: 'defaultBillingClassId',
            lookup: 'billing_class',
        }),
        { key: 'startDate', label: 'Start date', type: 'date', path: 'startDate', sortable: true },
        {
            key: 'endDate',
            label: 'End date',
            type: 'date',
            path: 'endDate',
            sortable: true,
            defaultColumn: true,
        },
        { key: 'isBillable', label: 'Billable', type: 'boolean', path: 'isBillable' },
        {
            key: 'isChargeCodeProject',
            label: 'Charge-code project',
            type: 'boolean',
            path: 'isChargeCodeProject',
        },
        {
            key: 'opportunityId',
            label: 'Source opportunity',
            type: 'uuid',
            path: 'opportunityId',
            column: false,
        },
        {
            key: 'managedServicesAgreementId',
            label: 'Bills against a managed-services agreement',
            type: 'uuid',
            path: 'managedServicesAgreementId',
            column: false,
        },
        {
            key: 'source',
            label: 'Origin',
            type: 'enum',
            path: 'source',
            options: SOURCE_OPTIONS,
            groupable: true,
        },
        {
            key: 'hasOpenTickets',
            label: 'Has open tickets',
            type: 'link',
            link: { path: 'tickets', where: { status: { is: { isClosed: false } } } },
        },
        {
            key: 'hasTimeEntries',
            label: 'Has time logged',
            type: 'link',
            link: { path: 'timeEntries' },
        },
        {
            key: 'hasUnbilledTime',
            label: 'Has unbilled billable time',
            type: 'link',
            link: {
                path: 'timeEntries',
                where: { billable: true, writtenOff: false, billedExternally: false, invoiceId: null },
            },
        },
        ...timestampFields(),
    ],
};
// ---------------------------------------------------------------------------
// Customers
// ---------------------------------------------------------------------------
const CUSTOMER_DEF = {
    entity: 'customers',
    label: DASHBOARD_ENTITY_LABELS.customers,
    area: 'customers',
    recordPath: '/customers?returnCustomerId={id}&returnTab=details',
    defaultSort: { field: 'name', dir: 'asc' },
    fields: [
        { key: 'name', label: 'Customer', type: 'string', path: 'name', sortable: true, defaultColumn: true },
        { key: 'aka', label: 'AKA', type: 'string', path: 'aka' },
        {
            key: 'netsuiteAccountId',
            label: 'NetSuite Account ID',
            type: 'string',
            path: 'netsuiteAccountId',
            sortable: true,
            defaultColumn: true,
        },
        {
            key: 'netsuiteAccountName',
            label: 'NetSuite Account Name',
            type: 'string',
            path: 'netsuiteAccountName',
        },
        {
            key: 'netsuiteStatus',
            label: 'NetSuite status',
            type: 'string',
            path: 'netsuiteStatus',
            groupable: true,
        },
        { key: 'goLiveDate', label: 'Go-live date', type: 'date', path: 'goLiveDate', sortable: true },
        {
            key: 'isActive',
            label: 'Active',
            type: 'boolean',
            path: 'isActive',
            defaultColumn: true,
        },
        {
            key: 'customerTypeId',
            label: 'Customer type',
            type: 'uuid',
            path: 'types[].customerTypeId',
            lookup: 'customer_type',
            column: false,
        },
        {
            key: 'qboCustomerId',
            label: 'QuickBooks ID',
            type: 'string',
            path: 'qboCustomerId',
        },
        {
            key: 'netTerms',
            label: 'Net terms',
            type: 'string',
            path: 'netTerms',
            groupable: true,
        },
        lookupNameField({
            key: 'defaultSupportAgreementName',
            label: 'Default support agreement',
            path: 'defaultSupportAgreement.name',
            groupPath: 'defaultSupportAgreementId',
            lookup: 'agreement',
        }),
        {
            key: 'defaultSupportAgreementId',
            label: 'Default support agreement (set / not set)',
            type: 'uuid',
            path: 'defaultSupportAgreementId',
            column: false,
        },
        { key: 'billingReminder', label: 'Billing reminder', type: 'string', path: 'billingReminder' },
        { key: 'groupEmail', label: 'Group email', type: 'string', path: 'groupEmail' },
        { key: 'city', label: 'City', type: 'string', path: 'city', groupable: true },
        { key: 'state', label: 'State', type: 'string', path: 'state', groupable: true },
        { key: 'phone', label: 'Phone', type: 'string', path: 'phone' },
        { key: 'website', label: 'Website', type: 'string', path: 'website' },
        { key: 'changeControlled', label: 'Change controlled', type: 'boolean', path: 'changeControlled' },
        { key: 'loeBeforeWork', label: 'LOE before work', type: 'boolean', path: 'loeBeforeWork' },
        {
            key: 'marketingAcquisition',
            label: 'Marketing acquisition',
            type: 'boolean',
            path: 'marketingAcquisition',
        },
        {
            key: 'verboseHourNotesRequired',
            label: 'Verbose hour entry notes required',
            type: 'boolean',
            path: 'verboseHourNotesRequired',
        },
        {
            key: 'documentBugDetail',
            label: 'Document bug vs non-bug in detail',
            type: 'boolean',
            path: 'documentBugDetail',
        },
        {
            key: 'source',
            label: 'Origin',
            type: 'enum',
            path: 'source',
            options: SOURCE_OPTIONS,
            groupable: true,
        },
        {
            key: 'hasActiveProjects',
            label: 'Has active projects',
            type: 'link',
            link: { path: 'projects', where: { status: 'active' } },
        },
        {
            key: 'hasActiveAgreements',
            label: 'Has active agreements',
            type: 'link',
            link: { path: 'agreements', where: { status: 'active' } },
        },
        {
            key: 'hasOpenOpportunities',
            label: 'Has open opportunities',
            type: 'link',
            link: { path: 'opportunities', where: { status: 'open' } },
        },
        { key: 'hasContacts', label: 'Has contacts', type: 'link', link: { path: 'contacts' } },
        {
            key: 'hasBillingContact',
            label: 'Has a billing contact',
            type: 'link',
            link: { path: 'contacts', where: { billingContact: true, isActive: true } },
        },
        ...timestampFields(),
    ],
};
// ---------------------------------------------------------------------------
// Onboarding
// ---------------------------------------------------------------------------
/** One filterable checklist step per onboarding step (stored in `stepsJson`). */
const ONBOARDING_STEP_FIELDS = ONBOARDING_STEPS.map((step) => ({
    key: `step_${step.key}`,
    label: `Step: ${step.label}`,
    type: 'step',
    path: 'stepsJson',
    stepKey: step.key,
    column: false,
}));
const ONBOARDING_DEF = {
    entity: 'onboarding',
    label: DASHBOARD_ENTITY_LABELS.onboarding,
    area: 'sales',
    recordPath: '/sales/onboarding/{id}',
    defaultSort: { field: 'createdAt', dir: 'desc' },
    fields: [
        lookupNameField({
            key: 'customerName',
            label: 'Customer',
            path: 'customer.name',
            groupPath: 'customerId',
            lookup: 'customer',
            defaultColumn: true,
        }),
        {
            key: 'customerId',
            label: 'Customer (pick one)',
            type: 'uuid',
            path: 'customerId',
            lookup: 'customer',
            column: false,
        },
        {
            key: 'opportunityName',
            label: 'Opportunity',
            type: 'string',
            path: 'opportunity.name',
            sortable: true,
            defaultColumn: true,
        },
        {
            key: 'opportunityStatus',
            label: 'Opportunity status',
            type: 'enum',
            path: 'opportunity.status',
            options: options(OPPORTUNITY_STATUSES, OPPORTUNITY_STATUS_LABELS),
            groupable: true,
            sortable: true,
            defaultColumn: true,
        },
        {
            key: 'status',
            label: 'Onboarding status',
            type: 'enum',
            path: 'status',
            options: options(ONBOARDING_STATUSES, ONBOARDING_STATUS_LABELS),
            groupable: true,
            sortable: true,
            defaultColumn: true,
        },
        {
            key: 'phase',
            label: 'Delivery phase',
            type: 'enum',
            path: 'phase',
            options: options(ONBOARDING_PHASES, ONBOARDING_PHASE_LABELS),
            groupable: true,
            sortable: true,
            defaultColumn: true,
        },
        { key: 'isException', label: 'Exception', type: 'boolean', path: 'isException', groupable: true },
        { key: 'exceptionNotes', label: 'Exception note', type: 'string', path: 'exceptionNotes' },
        lookupNameField({
            key: 'salesRepName',
            label: 'Sales rep',
            path: 'opportunity.salesRep.displayName',
            groupPath: 'opportunity.salesRepId',
            lookup: 'member',
        }),
        {
            key: 'salesRepId',
            label: 'Sales rep (pick one)',
            type: 'uuid',
            path: 'opportunity.salesRepId',
            lookup: 'member',
            column: false,
        },
        lookupNameField({
            key: 'opportunityTypeName',
            label: 'Opportunity type',
            path: 'opportunity.serviceCategory.name',
            groupPath: 'opportunity.serviceCategoryId',
            lookup: 'service_category',
        }),
        {
            key: 'agreementId',
            label: 'Linked agreement',
            type: 'uuid',
            path: 'agreementId',
            column: false,
        },
        {
            key: 'cuListId',
            label: 'ClickUp list',
            type: 'string',
            path: 'cuListId',
        },
        { key: 'msGroupEmail', label: 'MS group email', type: 'string', path: 'msGroupEmail' },
        {
            key: 'welcomeEnabled',
            label: 'Welcome link enabled',
            type: 'boolean',
            path: 'welcomeEnabled',
        },
        ...ONBOARDING_STEP_FIELDS,
        {
            key: 'hasProject',
            label: 'Has a PSA project',
            type: 'link',
            link: { path: 'opportunity.projects' },
        },
        {
            key: 'hasNotes',
            label: 'Has notes',
            type: 'link',
            link: { path: 'notes' },
        },
        ...timestampFields(),
    ],
};
// ---------------------------------------------------------------------------
// Tickets
// ---------------------------------------------------------------------------
const TICKET_DEF = {
    entity: 'tickets',
    label: DASHBOARD_ENTITY_LABELS.tickets,
    area: 'tickets',
    recordPath: '/tickets/{id}',
    defaultSort: { field: 'number', dir: 'desc' },
    fields: [
        {
            key: 'referenceId',
            label: 'Ticket ID',
            type: 'string',
            path: 'referenceId',
            sortable: true,
            defaultColumn: true,
        },
        { key: 'number', label: 'Number', type: 'number', path: 'number', sortable: true },
        { key: 'title', label: 'Title', type: 'string', path: 'title', sortable: true, defaultColumn: true },
        {
            key: 'origin',
            label: 'Type',
            type: 'enum',
            path: 'origin',
            options: options(TICKET_ORIGINS, { project: 'Project', support: 'Support' }),
            groupable: true,
            defaultColumn: true,
        },
        lookupNameField({
            key: 'statusName',
            label: 'Status',
            path: 'status.name',
            groupPath: 'statusId',
            lookup: 'ticket_status',
            defaultColumn: true,
        }),
        { key: 'isClosed', label: 'Closed', type: 'boolean', path: 'status.isClosed' },
        {
            key: 'priority',
            label: 'Priority',
            type: 'enum',
            path: 'priority',
            options: options(TICKET_PRIORITIES),
            groupable: true,
        },
        lookupNameField({
            key: 'ticketTypeName',
            label: 'Ticket type',
            path: 'ticketType.name',
            groupPath: 'ticketTypeId',
            lookup: 'ticket_type',
        }),
        {
            key: 'assigneeId',
            label: 'Assigned to',
            type: 'uuid',
            path: 'assignees[].memberId',
            lookup: 'member',
            column: false,
        },
        {
            key: 'projectName',
            label: 'Project',
            type: 'string',
            path: 'project.name',
            emptyPath: 'projectId',
            groupable: true,
            sortable: true,
        },
        {
            key: 'agreementName',
            label: 'Agreement',
            type: 'string',
            path: 'agreement.name',
            emptyPath: 'agreementId',
        },
        { key: 'contactEmail', label: 'Contact email', type: 'string', path: 'contact.email' },
        {
            key: 'budgetHours',
            label: 'Budget hours',
            type: 'hours',
            path: 'budgetHours',
            aggregatable: true,
            sortable: true,
        },
        { key: 'closedAt', label: 'Closed on', type: 'datetime', path: 'closedAt', sortable: true },
        { key: 'cuTaskId', label: 'ClickUp task id', type: 'string', path: 'cuTaskId' },
        {
            key: 'source',
            label: 'Origin',
            type: 'enum',
            path: 'source',
            options: SOURCE_OPTIONS,
            groupable: true,
        },
        { key: 'hasTime', label: 'Has time logged', type: 'link', link: { path: 'timeEntries' } },
        ...timestampFields(),
    ],
};
// ---------------------------------------------------------------------------
// Contacts
// ---------------------------------------------------------------------------
const CONTACT_DEF = {
    entity: 'contacts',
    label: DASHBOARD_ENTITY_LABELS.contacts,
    area: 'customers',
    recordPath: '/customers?returnCustomerId={customerId}&returnTab=contacts',
    recordPathFields: ['customerId'],
    defaultSort: { field: 'lastName', dir: 'asc' },
    fields: [
        { key: 'firstName', label: 'First name', type: 'string', path: 'firstName', sortable: true, defaultColumn: true },
        { key: 'lastName', label: 'Last name', type: 'string', path: 'lastName', sortable: true, defaultColumn: true },
        { key: 'email', label: 'Email', type: 'string', path: 'email', sortable: true, defaultColumn: true },
        { key: 'phone', label: 'Phone', type: 'string', path: 'phone' },
        { key: 'title', label: 'Job title', type: 'string', path: 'title' },
        lookupNameField({
            key: 'customerName',
            label: 'Customer',
            path: 'customer.name',
            groupPath: 'customerId',
            lookup: 'customer',
            defaultColumn: true,
        }),
        {
            key: 'customerId',
            label: 'Customer (pick one)',
            type: 'uuid',
            path: 'customerId',
            lookup: 'customer',
            column: false,
        },
        { key: 'isActive', label: 'Active', type: 'boolean', path: 'isActive' },
        { key: 'isPrimary', label: 'Primary contact', type: 'boolean', path: 'isPrimary' },
        { key: 'billingContact', label: 'Billing contact', type: 'boolean', path: 'billingContact' },
        { key: 'projectContact', label: 'Project contact', type: 'boolean', path: 'projectContact' },
        { key: 'supportContact', label: 'Support contact', type: 'boolean', path: 'supportContact' },
        { key: 'ticketManager', label: 'Ticket manager', type: 'boolean', path: 'ticketManager' },
        {
            key: 'sendToMailerlite',
            label: 'Send to MailerLite',
            type: 'boolean',
            path: 'sendToMailerlite',
        },
        {
            key: 'catPlusNotifications',
            label: 'Catalyst+ notifications',
            type: 'boolean',
            path: 'catPlusNotifications',
        },
        { key: 'referenceable', label: 'Referenceable', type: 'boolean', path: 'referenceable' },
        {
            key: 'supportTicketAccess',
            label: 'Support ticket access',
            type: 'enum',
            path: 'supportTicketAccess',
            options: options(SUPPORT_TICKET_ACCESS_LEVELS, SUPPORT_TICKET_ACCESS_LABELS),
            groupable: true,
        },
        {
            key: 'aiAccess',
            label: 'AI access',
            type: 'enum',
            path: 'aiAccess',
            options: options(AI_ACCESS_LEVELS, AI_ACCESS_LABELS),
            groupable: true,
        },
        {
            key: 'adminAccess',
            label: 'Admin access',
            type: 'enum',
            path: 'adminAccess',
            options: options(ADMIN_ACCESS_LEVELS, ADMIN_ACCESS_LABELS),
            groupable: true,
        },
        {
            key: 'lastReferenceRequest',
            label: 'Last reference request',
            type: 'date',
            path: 'lastReferenceRequest',
            sortable: true,
        },
        ...timestampFields(),
    ],
};
// ---------------------------------------------------------------------------
// Agreements
// ---------------------------------------------------------------------------
const AGREEMENT_DEF = {
    entity: 'agreements',
    label: DASHBOARD_ENTITY_LABELS.agreements,
    area: 'agreements',
    recordPath: '/agreements/{id}',
    defaultSort: { field: 'number', dir: 'desc' },
    fields: [
        { key: 'number', label: 'Number', type: 'number', path: 'number', sortable: true, defaultColumn: true },
        { key: 'name', label: 'Agreement', type: 'string', path: 'name', sortable: true, defaultColumn: true },
        lookupNameField({
            key: 'customerName',
            label: 'Customer',
            path: 'customer.name',
            groupPath: 'customerId',
            lookup: 'customer',
            defaultColumn: true,
        }),
        {
            key: 'customerId',
            label: 'Customer (pick one)',
            type: 'uuid',
            path: 'customerId',
            lookup: 'customer',
            column: false,
        },
        {
            key: 'type',
            label: 'Billing type',
            type: 'enum',
            path: 'type',
            options: options(AGREEMENT_TYPES, AGREEMENT_TYPE_LABELS),
            groupable: true,
            defaultColumn: true,
        },
        {
            key: 'status',
            label: 'Status',
            type: 'enum',
            path: 'status',
            options: options(AGREEMENT_STATUSES),
            groupable: true,
            sortable: true,
            defaultColumn: true,
        },
        lookupNameField({
            key: 'subtypeName',
            label: 'Subtype',
            path: 'subtype.name',
            groupPath: 'subtypeId',
            lookup: 'agreement_subtype',
        }),
        { key: 'startDate', label: 'Start date', type: 'date', path: 'startDate', sortable: true },
        { key: 'endDate', label: 'End date', type: 'date', path: 'endDate', sortable: true },
        {
            key: 'rateIncreaseDate',
            label: 'Rate increase date',
            type: 'date',
            path: 'rateIncreaseDate',
            sortable: true,
        },
        { key: 'customerPo', label: 'Customer PO', type: 'string', path: 'customerPo' },
        {
            key: 'hoursIncluded',
            label: 'Hours included',
            type: 'hours',
            path: 'hoursIncluded',
            aggregatable: true,
            sortable: true,
        },
        {
            key: 'overageRate',
            label: 'Overage rate',
            type: 'money',
            path: 'overageRate',
            aggregatable: true,
        },
        {
            key: 'source',
            label: 'Origin',
            type: 'enum',
            path: 'source',
            options: SOURCE_OPTIONS,
            groupable: true,
        },
        { key: 'hasLines', label: 'Has product lines', type: 'link', link: { path: 'lines' } },
        { key: 'hasInvoices', label: 'Has invoices', type: 'link', link: { path: 'invoices' } },
        ...timestampFields(),
    ],
};
// ---------------------------------------------------------------------------
// Opportunities
// ---------------------------------------------------------------------------
const OPPORTUNITY_DEF = {
    entity: 'opportunities',
    label: DASHBOARD_ENTITY_LABELS.opportunities,
    area: 'sales',
    recordPath: '/sales/opportunities/{id}',
    defaultSort: { field: 'closeDate', dir: 'asc' },
    fields: [
        { key: 'name', label: 'Opportunity', type: 'string', path: 'name', sortable: true, defaultColumn: true },
        lookupNameField({
            key: 'customerName',
            label: 'Customer',
            path: 'customer.name',
            groupPath: 'customerId',
            lookup: 'customer',
            defaultColumn: true,
        }),
        {
            key: 'customerId',
            label: 'Customer (pick one)',
            type: 'uuid',
            path: 'customerId',
            lookup: 'customer',
            column: false,
        },
        {
            key: 'stage',
            label: 'Stage',
            type: 'enum',
            path: 'stage',
            options: options(OPPORTUNITY_STAGES, OPPORTUNITY_STAGE_LABELS),
            groupable: true,
            sortable: true,
            defaultColumn: true,
        },
        {
            key: 'status',
            label: 'Status',
            type: 'enum',
            path: 'status',
            options: options(OPPORTUNITY_STATUSES, OPPORTUNITY_STATUS_LABELS),
            groupable: true,
            defaultColumn: true,
        },
        {
            key: 'kind',
            label: 'New or upsell',
            type: 'enum',
            path: 'kind',
            options: options(OPPORTUNITY_KINDS, OPPORTUNITY_KIND_LABELS),
            groupable: true,
        },
        {
            key: 'closeDate',
            label: 'Close date',
            type: 'date',
            path: 'closeDate',
            sortable: true,
            defaultColumn: true,
        },
        lookupNameField({
            key: 'salesRepName',
            label: 'Sales rep',
            path: 'salesRep.displayName',
            groupPath: 'salesRepId',
            lookup: 'member',
        }),
        {
            key: 'salesRepId',
            label: 'Sales rep (pick one)',
            type: 'uuid',
            path: 'salesRepId',
            lookup: 'member',
            column: false,
        },
        lookupNameField({
            key: 'serviceCategoryName',
            label: 'Opportunity type',
            path: 'serviceCategory.name',
            groupPath: 'serviceCategoryId',
            lookup: 'service_category',
        }),
        {
            key: 'lastReviewedDate',
            label: 'Last reviewed',
            type: 'date',
            path: 'lastReviewedDate',
            sortable: true,
        },
        { key: 'summary', label: 'Summary', type: 'string', path: 'summary' },
        { key: 'nsReg', label: 'NS Reg', type: 'string', path: 'nsReg' },
        { key: 'avalaraReg', label: 'Avalara Reg', type: 'string', path: 'avalaraReg' },
        {
            key: 'marketingAcquisition',
            label: 'Marketing acquisition',
            type: 'boolean',
            path: 'marketingAcquisition',
        },
        {
            key: 'lengthOfProject',
            label: 'Length of project (months)',
            type: 'number',
            path: 'lengthOfProject',
            aggregatable: true,
        },
        {
            key: 'lengthOfAgreement',
            label: 'Length of agreement (months)',
            type: 'number',
            path: 'lengthOfAgreement',
            aggregatable: true,
        },
        { key: 'cpqQuoteNumber', label: 'CPQ quote #', type: 'number', path: 'cpqQuoteNumber' },
        {
            key: 'source',
            label: 'Origin',
            type: 'enum',
            path: 'source',
            options: SOURCE_OPTIONS,
            groupable: true,
        },
        { key: 'hasProducts', label: 'Has product lines', type: 'link', link: { path: 'products' } },
        { key: 'hasQuotes', label: 'Has quotes', type: 'link', link: { path: 'quotes' } },
        { key: 'hasProject', label: 'Has a PSA project', type: 'link', link: { path: 'projects' } },
        ...timestampFields(),
    ],
};
// ---------------------------------------------------------------------------
// Quotes
// ---------------------------------------------------------------------------
const QUOTE_DEF = {
    entity: 'quotes',
    label: DASHBOARD_ENTITY_LABELS.quotes,
    area: 'sales',
    recordPath: '/sales/quotes/{id}',
    defaultSort: { field: 'createdAt', dir: 'desc' },
    fields: [
        lookupNameField({
            key: 'customerName',
            label: 'Customer',
            path: 'opportunity.customer.name',
            groupPath: 'opportunity.customerId',
            lookup: 'customer',
            defaultColumn: true,
        }),
        {
            key: 'customerId',
            label: 'Customer (pick one)',
            type: 'uuid',
            path: 'opportunity.customerId',
            lookup: 'customer',
            column: false,
        },
        {
            key: 'opportunityName',
            label: 'Opportunity',
            type: 'string',
            path: 'opportunity.name',
            sortable: true,
            defaultColumn: true,
        },
        {
            key: 'status',
            label: 'Status',
            type: 'enum',
            path: 'status',
            options: options(QUOTE_STATUSES, QUOTE_STATUS_LABELS),
            groupable: true,
            sortable: true,
            defaultColumn: true,
        },
        { key: 'version', label: 'Version', type: 'number', path: 'version', sortable: true, defaultColumn: true },
        { key: 'isLatest', label: 'Latest version', type: 'boolean', path: 'isLatest' },
        {
            key: 'sentAt',
            label: 'Sent to customer',
            type: 'datetime',
            path: 'sentAt',
            sortable: true,
        },
        {
            // Prisma relation count — selectable/readable as `_count.visits`, but not a real
            // column, so it can never be filtered, sorted, grouped or aggregated.
            key: 'visitCount',
            label: 'Customer visits',
            type: 'number',
            path: '_count.visits',
            filterable: false,
        },
        { key: 'total', label: 'Total', type: 'money', path: 'total', aggregatable: true, sortable: true, defaultColumn: true },
        {
            key: 'totalOneTime',
            label: 'Total one-time',
            type: 'money',
            path: 'totalOneTime',
            aggregatable: true,
            sortable: true,
        },
        {
            key: 'totalMonthly',
            label: 'Total monthly',
            type: 'money',
            path: 'totalMonthly',
            aggregatable: true,
            sortable: true,
        },
        {
            key: 'totalYearly',
            label: 'Total yearly',
            type: 'money',
            path: 'totalYearly',
            aggregatable: true,
            sortable: true,
        },
        lookupNameField({
            key: 'salesRepName',
            label: 'Sales rep',
            path: 'opportunity.salesRep.displayName',
            groupPath: 'opportunity.salesRepId',
            lookup: 'member',
        }),
        {
            key: 'salesRepId',
            label: 'Sales rep (pick one)',
            type: 'uuid',
            path: 'opportunity.salesRepId',
            lookup: 'member',
            column: false,
        },
        lookupNameField({
            key: 'opportunityTypeName',
            label: 'Opportunity type',
            path: 'opportunity.serviceCategory.name',
            groupPath: 'opportunity.serviceCategoryId',
            lookup: 'service_category',
        }),
        {
            key: 'opportunityStatus',
            label: 'Opportunity status',
            type: 'enum',
            path: 'opportunity.status',
            options: options(OPPORTUNITY_STATUSES, OPPORTUNITY_STATUS_LABELS),
            groupable: true,
            sortable: true,
        },
        {
            key: 'opportunityStage',
            label: 'Opportunity stage',
            type: 'enum',
            path: 'opportunity.stage',
            options: options(OPPORTUNITY_STAGES, OPPORTUNITY_STAGE_LABELS),
            groupable: true,
            sortable: true,
        },
        {
            key: 'opportunityCloseDate',
            label: 'Opportunity close date',
            type: 'date',
            path: 'opportunity.closeDate',
            sortable: true,
        },
        { key: 'approvedAt', label: 'Approved', type: 'datetime', path: 'approvedAt', sortable: true },
        ...timestampFields(),
    ],
};
// ---------------------------------------------------------------------------
// Invoices
// ---------------------------------------------------------------------------
const INVOICE_DEF = {
    entity: 'invoices',
    label: DASHBOARD_ENTITY_LABELS.invoices,
    area: 'invoices',
    recordPath: '/invoices/{id}',
    defaultSort: { field: 'number', dir: 'desc' },
    fields: [
        { key: 'number', label: 'Invoice #', type: 'number', path: 'seq', sortable: true, defaultColumn: true },
        lookupNameField({
            key: 'customerName',
            label: 'Customer',
            path: 'customer.name',
            groupPath: 'customerId',
            lookup: 'customer',
            defaultColumn: true,
        }),
        {
            key: 'customerId',
            label: 'Customer (pick one)',
            type: 'uuid',
            path: 'customerId',
            lookup: 'customer',
            column: false,
        },
        {
            key: 'status',
            label: 'Status',
            type: 'enum',
            path: 'status',
            options: options(INVOICE_STATUSES),
            groupable: true,
            sortable: true,
            defaultColumn: true,
        },
        {
            key: 'source',
            label: 'Kind',
            type: 'enum',
            path: 'source',
            options: options(INVOICE_SOURCES),
            groupable: true,
        },
        {
            key: 'documentType',
            label: 'Document type',
            type: 'enum',
            path: 'documentType',
            options: options(INVOICE_DOCUMENT_TYPES, INVOICE_DOCUMENT_TYPE_LABELS),
            groupable: true,
        },
        {
            key: 'total',
            label: 'Total',
            type: 'money',
            path: 'total',
            aggregatable: true,
            sortable: true,
            defaultColumn: true,
        },
        { key: 'subtotal', label: 'Subtotal', type: 'money', path: 'subtotal', aggregatable: true },
        {
            key: 'issueDate',
            label: 'Invoice date',
            type: 'date',
            path: 'issueDate',
            sortable: true,
            defaultColumn: true,
        },
        { key: 'dueDate', label: 'Due date', type: 'date', path: 'dueDate', sortable: true },
        { key: 'terms', label: 'Terms', type: 'string', path: 'terms', groupable: true },
        lookupNameField({
            key: 'serviceCategoryName',
            label: 'Service category',
            path: 'serviceCategory.name',
            groupPath: 'serviceCategoryId',
            lookup: 'service_category',
        }),
        {
            key: 'agreementName',
            label: 'Agreement',
            type: 'string',
            path: 'agreement.name',
            emptyPath: 'agreementId',
        },
        {
            key: 'projectName',
            label: 'Project',
            type: 'string',
            path: 'project.name',
            emptyPath: 'projectId',
        },
        { key: 'needsChange', label: 'Needs change', type: 'boolean', path: 'needsChange' },
        {
            key: 'createdExternally',
            label: 'Billed outside PSA',
            type: 'boolean',
            path: 'createdExternally',
        },
        {
            key: 'activeReviewerId',
            label: 'Reviewer',
            type: 'uuid',
            path: 'activeReviewerId',
            lookup: 'member',
            column: false,
        },
        ...timestampFields(),
    ],
};
// ---------------------------------------------------------------------------
// Time entries (and the billing-audit view of the same records)
// ---------------------------------------------------------------------------
function timeEntryFields() {
    return [
        { key: 'workDate', label: 'Work date', type: 'date', path: 'workDate', sortable: true, defaultColumn: true },
        {
            key: 'hours',
            label: 'Hours',
            type: 'hours',
            path: 'hours',
            aggregatable: true,
            sortable: true,
            defaultColumn: true,
        },
        lookupNameField({
            key: 'memberName',
            label: 'Member',
            path: 'member.displayName',
            groupPath: 'memberId',
            lookup: 'member',
            defaultColumn: true,
        }),
        {
            key: 'memberId',
            label: 'Member (pick one)',
            type: 'uuid',
            path: 'memberId',
            lookup: 'member',
            column: false,
        },
        lookupNameField({
            key: 'serviceCategoryName',
            label: 'Service category',
            path: 'serviceCategory.name',
            groupPath: 'serviceCategoryId',
            lookup: 'service_category',
        }),
        lookupNameField({
            key: 'billingClassName',
            label: 'Billing class',
            path: 'billingClass.name',
            groupPath: 'billingClassId',
            lookup: 'billing_class',
        }),
        {
            key: 'projectName',
            label: 'Project',
            type: 'string',
            path: 'project.name',
            emptyPath: 'projectId',
            sortable: true,
        },
        {
            key: 'agreementName',
            label: 'Agreement',
            type: 'string',
            path: 'agreement.name',
            emptyPath: 'agreementId',
        },
        {
            key: 'ticketTitle',
            label: 'Ticket',
            type: 'string',
            path: 'ticket.title',
            emptyPath: 'ticketId',
            defaultColumn: true,
        },
        { key: 'ticketReferenceId', label: 'Ticket ID', type: 'string', path: 'ticket.referenceId' },
        { key: 'billable', label: 'Billable', type: 'boolean', path: 'billable', groupable: true },
        { key: 'notes', label: 'Notes', type: 'string', path: 'notes' },
        {
            key: 'resolvedRate',
            label: 'Rate',
            type: 'money',
            path: 'resolvedRate',
            aggregatable: true,
        },
    ];
}
const TIME_ENTRY_DEF = {
    entity: 'time_entries',
    label: DASHBOARD_ENTITY_LABELS.time_entries,
    area: 'time',
    recordPath: '/time-entries/{id}',
    defaultSort: { field: 'workDate', dir: 'desc' },
    fields: [
        ...timeEntryFields(),
        { key: 'writtenOff', label: 'Written off', type: 'boolean', path: 'writtenOff' },
        {
            key: 'invoiceId',
            label: 'On a PSA invoice',
            type: 'uuid',
            path: 'invoiceId',
            column: false,
        },
        {
            key: 'source',
            label: 'Origin',
            type: 'enum',
            path: 'source',
            options: SOURCE_OPTIONS,
            groupable: true,
        },
        {
            key: 'entryMethod',
            label: 'Entered via',
            type: 'enum',
            path: 'entryMethod',
            options: ENTRY_METHOD_OPTIONS,
            groupable: true,
        },
        ...timestampFields(),
    ],
};
const BILLING_AUDIT_DEF = {
    entity: 'billing_audit',
    label: DASHBOARD_ENTITY_LABELS.billing_audit,
    area: 'invoices',
    recordPath: '/time-entries/{id}',
    defaultSort: { field: 'workDate', dir: 'desc' },
    fields: [
        ...timeEntryFields(),
        { key: 'writtenOff', label: 'Written off', type: 'boolean', path: 'writtenOff' },
        { key: 'writtenOffAt', label: 'Written off on', type: 'datetime', path: 'writtenOffAt' },
        { key: 'writtenOffByName', label: 'Written off by', type: 'string', path: 'writtenOffByName' },
        {
            key: 'billedExternally',
            label: 'Billed outside PSA',
            type: 'boolean',
            path: 'billedExternally',
            groupable: true,
        },
        {
            key: 'externalInvoiceRef',
            label: 'External invoice reference',
            type: 'string',
            path: 'externalInvoiceRef',
        },
        {
            key: 'externalInvoiceDate',
            label: 'External invoice date',
            type: 'date',
            path: 'externalInvoiceDate',
            sortable: true,
        },
        {
            key: 'invoiceId',
            label: 'On a PSA invoice',
            type: 'uuid',
            path: 'invoiceId',
            column: false,
        },
        { key: 'invoicedAt', label: 'Invoiced on', type: 'datetime', path: 'invoicedAt', sortable: true },
        { key: 'rateSource', label: 'Rate source', type: 'string', path: 'rateSource', groupable: true },
        { key: 'rateOverride', label: 'Rate override', type: 'money', path: 'rateOverride' },
        ...timestampFields(),
    ],
};
// ---------------------------------------------------------------------------
// Timesheets (and the approvals queue over the same records)
// ---------------------------------------------------------------------------
const TIMING_OPTIONS = [
    { value: 'on_time', label: 'On time' },
    { value: 'past_due', label: 'Past due' },
];
function timesheetFields() {
    return [
        {
            key: 'weekStartDate',
            label: 'Week of',
            type: 'date',
            path: 'weekStartDate',
            sortable: true,
            defaultColumn: true,
        },
        lookupNameField({
            key: 'memberName',
            label: 'Member',
            path: 'member.displayName',
            groupPath: 'memberId',
            lookup: 'member',
            defaultColumn: true,
        }),
        {
            key: 'memberId',
            label: 'Member (pick one)',
            type: 'uuid',
            path: 'memberId',
            lookup: 'member',
            column: false,
        },
        {
            key: 'status',
            label: 'Status',
            type: 'enum',
            path: 'status',
            options: options(TIMESHEET_STATUSES),
            groupable: true,
            sortable: true,
            defaultColumn: true,
        },
        {
            key: 'approverId',
            label: 'Waiting on (approver)',
            type: 'uuid',
            path: 'approverId',
            lookup: 'member',
            column: false,
        },
        {
            key: 'submittedAt',
            label: 'Submitted',
            type: 'datetime',
            path: 'submittedAt',
            sortable: true,
            defaultColumn: true,
        },
        { key: 'approvedAt', label: 'Approved', type: 'datetime', path: 'approvedAt', sortable: true },
        { key: 'dueAt', label: 'Due', type: 'datetime', path: 'dueAt', sortable: true },
        {
            key: 'submissionTiming',
            label: 'On time / past due',
            type: 'enum',
            path: 'submissionTiming',
            options: TIMING_OPTIONS,
            groupable: true,
        },
        {
            key: 'bonusDisqualified',
            label: 'Bonus disqualified',
            type: 'boolean',
            path: 'bonusDisqualified',
        },
        { key: 'notes', label: 'Notes', type: 'string', path: 'notes' },
        { key: 'hasTime', label: 'Has time entries', type: 'link', link: { path: 'timeEntries' } },
        ...timestampFields(),
    ];
}
const TIMESHEET_DEF = {
    entity: 'timesheets',
    label: DASHBOARD_ENTITY_LABELS.timesheets,
    area: 'time',
    recordPath: '/timesheets/{id}',
    defaultSort: { field: 'weekStartDate', dir: 'desc' },
    fields: timesheetFields(),
};
const APPROVAL_DEF = {
    entity: 'approvals',
    label: DASHBOARD_ENTITY_LABELS.approvals,
    area: 'time',
    // Approvals are the submitted weeks waiting on someone — the same records as
    // Timesheets, permanently narrowed to the queue.
    baseWhere: { status: 'submitted' },
    recordPath: '/timesheets/{id}',
    defaultSort: { field: 'submittedAt', dir: 'asc' },
    fields: timesheetFields(),
};
// ---------------------------------------------------------------------------
// Catalog
// ---------------------------------------------------------------------------
/**
 * Entities wired to the query engine. The card builder only offers these; anything else
 * in {@link DASHBOARD_ENTITIES} is rejected until it is added here.
 */
export const DASHBOARD_ENTITY_DEFS = [
    PROJECT_DEF,
    TICKET_DEF,
    CUSTOMER_DEF,
    CONTACT_DEF,
    ONBOARDING_DEF,
    AGREEMENT_DEF,
    OPPORTUNITY_DEF,
    QUOTE_DEF,
    INVOICE_DEF,
    TIME_ENTRY_DEF,
    TIMESHEET_DEF,
    APPROVAL_DEF,
    BILLING_AUDIT_DEF,
];
const DEFS_BY_ENTITY = new Map(DASHBOARD_ENTITY_DEFS.map((def) => [def.entity, def]));
/** Looks up an entity definition, or undefined when the entity is not wired up. */
export function dashboardEntityDef(entity) {
    return DEFS_BY_ENTITY.get(entity);
}
/** Looks up one field on an entity, or undefined when it is not in the catalog. */
export function dashboardField(entity, fieldKey) {
    return dashboardEntityDef(entity)?.fields.find((f) => f.key === fieldKey);
}
/** Whether a field may be used as a filter (default true, except `link`-less specs). */
export function isFilterable(field) {
    return field.filterable !== false;
}
/** Whether a field may be shown as a list column. */
export function isColumn(field) {
    return field.column !== false && field.type !== 'link' && field.type !== 'step';
}
//# sourceMappingURL=dashboard-fields.js.map